import React from "react";
import { Card, Empty, Tabs, Typography } from "antd";
import type {
  DataQuestionThreadSummary,
  DataQuestionTurn,
} from "../../store/api/dataQuestionsApi";
import { formatRelativeDateTime } from "../../utils/relative-date";
import styles from "./styles.module.css";

interface ThreadsSidebarProps {
  threads: DataQuestionThreadSummary[];
  saved: DataQuestionTurn[];
  activeThreadId?: number;
  onOpen: (threadId: number) => void;
}

interface Row {
  key: number;
  threadId: number;
  title: string;
  meta: string;
}

const threadRows = (threads: DataQuestionThreadSummary[]): Row[] =>
  threads.map((thread) => ({
    key: thread.threadId,
    threadId: thread.threadId,
    title: thread.title,
    meta: `${formatRelativeDateTime(thread.updatedAt)} · ${
      thread.turns === 1 ? "1 pregunta" : `${thread.turns} preguntas`
    }`,
  }));

const savedRows = (saved: DataQuestionTurn[]): Row[] =>
  saved.map((turn) => ({
    key: turn.turnId,
    threadId: turn.threadId,
    title: turn.savedName ?? turn.question,
    meta: turn.question,
  }));

/** Recent threads of the current admin and the queries the team saved. */
export const ThreadsSidebar: React.FC<ThreadsSidebarProps> = ({
  threads,
  saved,
  activeThreadId,
  onOpen,
}) => {
  const list = (rows: Row[], emptyText: string) =>
    rows.length === 0 ? (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} />
    ) : (
      <ul className={styles.threadList}>
        {rows.map((row) => (
          <li key={row.key}>
            <button
              type="button"
              className={`${styles.threadItem} ${
                row.threadId === activeThreadId ? styles.threadItemActive : ""
              }`}
              aria-current={row.threadId === activeThreadId ? "true" : undefined}
              data-testid={`thread-item-${row.key}`}
              onClick={() => onOpen(row.threadId)}
            >
              <span className={styles.threadTitle}>{row.title}</span>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {row.meta}
              </Typography.Text>
            </button>
          </li>
        ))}
      </ul>
    );

  return (
    <Card size="small" styles={{ body: { padding: "4px 12px 12px" } }}>
      <Tabs
        size="small"
        items={[
          {
            key: "recent",
            label: "Recientes",
            children: list(threadRows(threads), "Sin consultas"),
          },
          {
            key: "saved",
            label: "Guardadas",
            children: list(savedRows(saved), "Nada guardado"),
          },
        ]}
      />
    </Card>
  );
};
